import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { ChevronLeft, ChevronRight, Clock, Plus, Trash2, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, addDays, startOfWeek, isSameDay, parseISO, isBefore } from "date-fns";
import { cn } from "@/lib/utils";
import { TIMEZONE_OPTIONS, getBrowserTimezone, getTimezoneAbbr } from "@/lib/timezone";

type DragState = {
  slotId: string;
  edge: "top" | "bottom" | "move";
  originY: number;
  originX: number;
  originalStart: string;
  originalEnd: string;
  currentStart: string;
  currentEnd: string;
  originalDate: string;
  currentDate: string;
};

type CreateDragState = {
  date: Date;
  anchorTime: string;
  currentTime: string;
};

const TIME_OPTIONS = (() => {
  const times: string[] = [];
  for (let h = 7; h <= 20; h++) {
    for (let m = 0; m < 60; m += 30) {
      times.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return times;
})();

const formatTime12 = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
};

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7);

const formatHour = (h: number) => {
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  return h > 12 ? `${h - 12} PM` : `${h} AM`;
};

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export interface WeeklyBlock {
  id: string;
  start_time: string;
  end_time: string;
}

interface AvailabilitySlot {
  id: string;
  specific_date: string | null;
  day_of_week: number | null;
  start_time: string;
  end_time: string;
}

const timeToOffset = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  return h - 7 + m / 60;
};

const CELL_HEIGHT = 64; // px per hour row (matches h-16)

const DEFAULT_START = "09:00";
const DEFAULT_END = "17:00";

const ProviderAvailability = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [weeklyTemplateOpen, setWeeklyTemplateOpen] = useState(false);
  const [providerTz, setProviderTz] = useState(getBrowserTimezone());

  // Fetch provider timezone
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("timezone")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        if (data?.timezone) setProviderTz(data.timezone);
      });
  }, [user]);

  const updateTimezone = async (newTz: string) => {
    setProviderTz(newTz);
    if (!user) return;
    await supabase
      .from("profiles")
      .update({ timezone: newTz } as any)
      .eq("user_id", user.id);
    toast({ title: "Timezone updated", description: TIMEZONE_OPTIONS.find(t => t.value === newTz)?.label || newTz });
  };

  // weekly template: day_of_week blocks (no specific_date)
  const weekly = useMemo(() => {
    const result: WeeklyBlock[][] = Array.from({ length: 7 }, () => []);
    slots.forEach((s) => {
      if (s.day_of_week !== null && !s.specific_date) {
        result[s.day_of_week].push({
          id: s.id,
          start_time: s.start_time.slice(0, 5),
          end_time: s.end_time.slice(0, 5),
        });
      }
    });
    return result;
  }, [slots]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const fetchSlots = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("provider_availability")
      .select("*")
      .eq("provider_id", user.id)
      .order("start_time");
    if (data) setSlots(data as AvailabilitySlot[]);
  }, [user]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Get specific-date slots for a given date
  const getSlotsForDate = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return slots.filter((s) => s.specific_date === dateStr);
  };

  // Get weekly template slots that apply to a given day (day_of_week, Mon=0..Sun=6)
  const getWeeklyForDay = (date: Date) => {
    // Our day_of_week: 0=Mon..6=Sun. JS getDay(): 0=Sun..6=Sat
    const jsDay = date.getDay();
    const dow = jsDay === 0 ? 6 : jsDay - 1;
    return weekly[dow];
  };

  // Drag-to-create state
  const [createDrag, setCreateDrag] = useState<CreateDragState | null>(null);

  const yToTime = (y: number, containerHeight: number): string => {
    const totalHours = HOURS.length; // 14 hours (7-21)
    const ratio = Math.max(0, Math.min(1, y / containerHeight));
    const totalMinutes = ratio * totalHours * 60;
    const snapped = Math.round(totalMinutes / 30) * 30;
    const h = 7 + Math.floor(snapped / 60);
    const m = snapped % 60;
    return `${String(Math.min(h, 21)).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const handleColumnMouseDown = (date: Date, e: React.MouseEvent<HTMLDivElement>) => {
    if (!user) return;
    if ((e.target as HTMLElement).closest("[data-slot]")) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(y, rect.height);
    setCreateDrag({ date, anchorTime: time, currentTime: time });
  };

  const handleColumnMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!createDrag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(y, rect.height);
    setCreateDrag((d) => d ? { ...d, currentTime: time } : null);
  };

  const handleColumnMouseUp = async () => {
    if (!createDrag || !user) return;
    const { date, anchorTime, currentTime } = createDrag;
    setCreateDrag(null);

    let start = anchorTime < currentTime ? anchorTime : currentTime;
    let end = anchorTime < currentTime ? currentTime : anchorTime;

    // If they just clicked (no drag), create a 1-hour block
    if (start === end) {
      const startOffset = timeToOffset(start);
      end = offsetToTime(Math.min(startOffset + 1, 14));
    }

    if (start >= end) return;

    const dateStr = format(date, "yyyy-MM-dd");
    const { data, error } = await supabase
      .from("provider_availability")
      .insert({ provider_id: user.id, specific_date: dateStr, start_time: start, end_time: end } as any)
      .select()
      .single();
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else if (data) {
      setSlots((prev) => [...prev, data as AvailabilitySlot]);
      toast({ title: "Availability added", description: `${formatTime12(start)} – ${formatTime12(end)}` });
    }
  };

  const removeSlot = async (id: string) => {
    await supabase.from("provider_availability").delete().eq("id", id);
    setSlots((prev) => prev.filter((s) => s.id !== id));
    toast({ title: "Slot removed" });
  };

  // Weekly template CRUD
  const addWeeklyBlock = async (dayIndex: number) => {
    if (!user) return;
    const existing = weekly[dayIndex];
    let start = DEFAULT_START;
    let end = DEFAULT_END;
    if (existing.length > 0) {
      const last = existing[existing.length - 1];
      const lastEndIdx = TIME_OPTIONS.indexOf(last.end_time);
      if (lastEndIdx >= 0 && lastEndIdx + 1 < TIME_OPTIONS.length) {
        start = TIME_OPTIONS[lastEndIdx + 1];
        end = TIME_OPTIONS[Math.min(lastEndIdx + 5, TIME_OPTIONS.length - 1)];
      }
    }
    if (start >= end) {
      toast({ title: "No room", variant: "destructive" });
      return;
    }
    const { data, error } = await supabase
      .from("provider_availability")
      .insert({ provider_id: user.id, day_of_week: dayIndex, start_time: start, end_time: end } as any)
      .select()
      .single();
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else if (data) {
      setSlots((prev) => [...prev, data as AvailabilitySlot]);
    }
  };

  const updateWeeklyBlock = async (blockId: string, start: string, end: string) => {
    if (start >= end) {
      toast({ title: "Invalid range", variant: "destructive" });
      return;
    }
    await supabase.from("provider_availability").update({ start_time: start, end_time: end }).eq("id", blockId);
    setSlots((prev) =>
      prev.map((s) => (s.id === blockId ? { ...s, start_time: start, end_time: end } : s))
    );
  };

  const removeWeeklyBlock = async (blockId: string) => {
    await supabase.from("provider_availability").delete().eq("id", blockId);
    setSlots((prev) => prev.filter((s) => s.id !== blockId));
    toast({ title: "Block removed" });
  };

  const copyTemplateToWeek = async () => {
    if (!user) return;
    const allBlocks = weekly.flatMap((blocks, i) =>
      blocks.map((b) => ({ dayIndex: i, ...b }))
    );
    if (allBlocks.length === 0) {
      toast({ title: "No weekly template to copy", variant: "destructive" });
      return;
    }
    const inserts = allBlocks.map((b) => ({
      provider_id: user.id,
      specific_date: format(addDays(weekStart, b.dayIndex), "yyyy-MM-dd"),
      start_time: b.start_time,
      end_time: b.end_time,
    }));
    const { error } = await supabase.from("provider_availability").insert(inserts as any);
    if (error) {
      const msg = error.message.includes("duplicate") ? "Some slots already exist." : error.message;
      toast({ title: "Error", description: msg, variant: "destructive" });
    } else {
      toast({ title: "Template applied to this week", description: `${inserts.length} slots created.` });
      fetchSlots();
    }
  };

  const isToday = (date: Date) => isSameDay(date, new Date());
  const isPast = (date: Date) => isBefore(date, new Date(new Date().setHours(0, 0, 0, 0)));

  // Drag-to-resize/move state
  const [drag, setDrag] = useState<DragState | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);

  const offsetToTime = (offset: number): string => {
    const snapped = Math.round(offset * 2) / 2; // 30 min snap
    const clamped = Math.max(0, Math.min(14, snapped)); // 7AM-9PM range
    const h = Math.floor(clamped) + 7;
    const m = (clamped % 1) * 60;
    return `${String(h).padStart(2, "0")}:${String(Math.round(m)).padStart(2, "0")}`;
  };

  const getDateFromX = (clientX: number): string | null => {
    for (let i = 0; i < columnRefs.current.length; i++) {
      const col = columnRefs.current[i];
      if (!col) continue;
      const rect = col.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        return format(weekDays[i], "yyyy-MM-dd");
      }
    }
    return null;
  };

  const handleDragStart = (e: React.MouseEvent, slotId: string, edge: "top" | "bottom" | "move", startTime: string, endTime: string, slotDate: string) => {
    e.stopPropagation();
    e.preventDefault();
    setDrag({
      slotId,
      edge,
      originY: e.clientY,
      originX: e.clientX,
      originalStart: startTime,
      originalEnd: endTime,
      currentStart: startTime,
      currentEnd: endTime,
      originalDate: slotDate,
      currentDate: slotDate,
    });
  };

  useEffect(() => {
    if (!drag) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - drag.originY;
      const deltaHours = deltaY / CELL_HEIGHT;

      if (drag.edge === "move") {
        const origStartOffset = timeToOffset(drag.originalStart);
        const origEndOffset = timeToOffset(drag.originalEnd);
        const duration = origEndOffset - origStartOffset;
        const newStartOffset = origStartOffset + deltaHours;
        const clampedStart = Math.max(0, Math.min(14 - duration, newStartOffset));
        const newStart = offsetToTime(clampedStart);
        const newEnd = offsetToTime(clampedStart + duration);
        const hoveredDate = getDateFromX(e.clientX);
        setDrag((d) => d ? { ...d, currentStart: newStart, currentEnd: newEnd, currentDate: hoveredDate || d.currentDate } : null);
      } else if (drag.edge === "bottom") {
        const origOffset = timeToOffset(drag.originalEnd);
        const newTime = offsetToTime(origOffset + deltaHours);
        if (newTime > drag.currentStart) {
          setDrag((d) => d ? { ...d, currentEnd: newTime } : null);
        }
      } else {
        const origOffset = timeToOffset(drag.originalStart);
        const newTime = offsetToTime(origOffset + deltaHours);
        if (newTime < drag.currentEnd) {
          setDrag((d) => d ? { ...d, currentStart: newTime } : null);
        }
      }
    };

    const handleMouseUp = async () => {
      if (!drag) return;
      const { slotId, currentStart, currentEnd, originalStart, originalEnd, originalDate, currentDate } = drag;
      setDrag(null);

      const changed = currentStart !== originalStart || currentEnd !== originalEnd || currentDate !== originalDate;
      if (!changed) return;
      if (currentStart >= currentEnd) return;

      const updatePayload: any = { start_time: currentStart, end_time: currentEnd };
      if (currentDate !== originalDate) {
        updatePayload.specific_date = currentDate;
      }

      await supabase
        .from("provider_availability")
        .update(updatePayload)
        .eq("id", slotId);

      setSlots((prev) =>
        prev.map((s) => s.id === slotId ? { ...s, start_time: currentStart, end_time: currentEnd, specific_date: currentDate } : s)
      );
      const dayLabel = currentDate !== originalDate ? ` on ${currentDate}` : "";
      toast({ title: "Availability updated", description: `${formatTime12(currentStart)} – ${formatTime12(currentEnd)}${dayLabel}` });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [drag]);

  return (
    <div className="space-y-4">
      {/* Header with week navigation */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Clock size={20} className="text-primary" />
          <h2 className="font-display text-xl text-foreground">Your Availability</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={providerTz} onValueChange={updateTimezone}>
            <SelectTrigger className="w-[160px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONE_OPTIONS.map((tz) => (
                <SelectItem key={tz.value} value={tz.value} className="text-xs">{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setWeekStart((d) => addDays(d, -7))}>
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm font-medium text-foreground min-w-[180px] text-center">
            {format(weekDays[0], "MMM d")} – {format(weekDays[6], "MMM d, yyyy")}
          </span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setWeekStart((d) => addDays(d, 7))}>
            <ChevronRight size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs ml-2"
            onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
          >
            Today
          </Button>
        </div>
      </div>

      {/* Legend & actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-primary/15 border border-primary/30" /> Available (template)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-primary/80" /> Scheduled
          </span>
          <span className="text-muted-foreground/60">Click or drag on the grid to add availability</span>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={copyTemplateToWeek}>
          <Copy size={12} /> Apply template to this week
        </Button>
      </div>

      {/* Calendar grid */}
      <Card className="border-border bg-card overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[700px]">
              {/* Day headers */}
              <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border">
                <div className="p-2" />
                {weekDays.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      "p-2 text-center border-l border-border",
                      isToday(day) && "bg-primary/5"
                    )}
                  >
                    <p className="text-xs font-medium text-muted-foreground">{format(day, "EEE")}</p>
                    <p className={cn("text-lg font-bold", isToday(day) ? "text-primary" : "text-foreground")}>
                      {format(day, "d")}
                    </p>
                  </div>
                ))}
              </div>

              {/* Time grid */}
              <div className="relative">
                <div className="grid grid-cols-[60px_repeat(7,1fr)]">
                  {HOURS.map((hour) => (
                    <div key={hour} className="contents">
                      <div className="h-16 flex items-start justify-end pr-2 pt-0.5">
                        <span className="text-[10px] text-muted-foreground">{formatHour(hour)}</span>
                      </div>
                      {weekDays.map((day) => (
                        <div
                          key={`${hour}-${day.toISOString()}`}
                          className={cn(
                            "h-16 border-l border-t border-border relative",
                            isToday(day) && "bg-primary/[0.02]"
                          )}
                        />
                      ))}
                    </div>
                  ))}
                </div>

                {/* Overlay: availability blocks */}
                <div className="absolute inset-0 grid grid-cols-[60px_repeat(7,1fr)] pointer-events-none">
                  <div />
                  {weekDays.map((day, dayIdx) => {
                    const dateSlots = getSlotsForDate(day);
                    const templateBlocks = getWeeklyForDay(day);
                    const datePast = isPast(day);

                    return (
                      <div
                        key={dayIdx}
                        ref={(el) => { columnRefs.current[dayIdx] = el; }}
                        className={cn(
                          "relative border-l border-border pointer-events-auto select-none",
                          !datePast && "cursor-crosshair"
                        )}
                        onMouseDown={datePast ? undefined : (e) => handleColumnMouseDown(day, e)}
                        onMouseMove={datePast ? undefined : handleColumnMouseMove}
                        onMouseUp={datePast ? undefined : handleColumnMouseUp}
                        title={datePast ? undefined : "Click or drag to add availability"}
                      >
                        {/* Weekly template blocks (ghost) — only show if no specific-date slots */}
                        {dateSlots.length === 0 &&
                          templateBlocks.map((block) => {
                            const top = timeToOffset(block.start_time) * CELL_HEIGHT;
                            const height = (timeToOffset(block.end_time) - timeToOffset(block.start_time)) * CELL_HEIGHT;
                            if (height <= 0) return null;
                            return (
                              <div
                                key={block.id}
                                className="absolute left-0.5 right-0.5 rounded-md bg-primary/10 border border-dashed border-primary/20"
                                style={{ top: `${top}px`, height: `${height}px` }}
                              >
                                <div className="px-1.5 py-0.5 text-[10px] text-primary/50 leading-tight truncate">
                                  {formatTime12(block.start_time)}
                                </div>
                              </div>
                            );
                          })}

                        {/* Specific-date slots (solid) with drag-to-resize/move */}
                        {dateSlots.map((slot) => {
                          const isDragging = drag?.slotId === slot.id;
                          // If this block is being moved to a different day, hide it from source column
                          if (isDragging && drag.edge === "move" && drag.currentDate !== format(day, "yyyy-MM-dd")) return null;
                          const st = isDragging ? drag.currentStart : slot.start_time.slice(0, 5);
                          const et = isDragging ? drag.currentEnd : slot.end_time.slice(0, 5);
                          const top = timeToOffset(st) * CELL_HEIGHT;
                          const height = (timeToOffset(et) - timeToOffset(st)) * CELL_HEIGHT;
                          if (height <= 0) return null;
                          const dateStr = format(day, "yyyy-MM-dd");
                          return (
                            <div
                              key={slot.id}
                              data-slot
                              className={cn(
                                "absolute left-1 right-1 rounded-md px-1.5 py-0.5 group select-none",
                                "bg-primary/80 text-primary-foreground border border-primary",
                                datePast && "opacity-50",
                                isDragging && "ring-2 ring-primary/50 z-10",
                                isDragging && drag.edge === "move" && "cursor-grabbing",
                                !datePast && !isDragging && "cursor-grab"
                              )}
                              style={{ top: `${top}px`, height: `${Math.max(height, 20)}px` }}
                              onMouseDown={datePast ? undefined : (e) => {
                                if ((e.target as HTMLElement).closest("[data-resize]") || (e.target as HTMLElement).closest("button")) return;
                                handleDragStart(e, slot.id, "move", slot.start_time.slice(0, 5), slot.end_time.slice(0, 5), dateStr);
                              }}
                            >
                              {/* Top resize handle */}
                              {!datePast && (
                                <div
                                  data-resize
                                  className="absolute top-0 left-0 right-0 h-2 cursor-n-resize hover:bg-primary-foreground/20 rounded-t-md z-10"
                                  onMouseDown={(e) => handleDragStart(e, slot.id, "top", slot.start_time.slice(0, 5), slot.end_time.slice(0, 5), dateStr)}
                                />
                              )}
                              <span className="text-[10px] font-medium leading-tight block truncate pointer-events-none">
                                {formatTime12(st)}
                              </span>
                              {height >= 28 && (
                                <span className="text-[10px] leading-tight block truncate opacity-80 pointer-events-none">
                                  {formatTime12(et)}
                                </span>
                              )}
                              {/* Bottom resize handle */}
                              {!datePast && (
                                <div
                                  data-resize
                                  className="absolute bottom-0 left-0 right-0 h-2 cursor-s-resize hover:bg-primary-foreground/20 rounded-b-md z-10"
                                  onMouseDown={(e) => handleDragStart(e, slot.id, "bottom", slot.start_time.slice(0, 5), slot.end_time.slice(0, 5), dateStr)}
                                />
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeSlot(slot.id);
                                }}
                                className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-destructive/80 rounded-sm p-0.5"
                                title="Remove slot"
                              >
                                <Trash2 size={10} className="text-destructive-foreground" />
                              </button>
                            </div>
                          );
                        })}

                        {/* Ghost block when dragging a slot INTO this column from another day */}
                        {drag && drag.edge === "move" && drag.currentDate === format(day, "yyyy-MM-dd") && drag.originalDate !== format(day, "yyyy-MM-dd") && (() => {
                          const top = timeToOffset(drag.currentStart) * CELL_HEIGHT;
                          const height = (timeToOffset(drag.currentEnd) - timeToOffset(drag.currentStart)) * CELL_HEIGHT;
                          if (height <= 0) return null;
                          return (
                            <div
                              data-slot
                              className="absolute left-1 right-1 rounded-md px-1.5 py-0.5 select-none bg-primary/80 text-primary-foreground border border-primary ring-2 ring-primary/50 z-10 cursor-grabbing pointer-events-none"
                              style={{ top: `${top}px`, height: `${Math.max(height, 20)}px` }}
                            >
                              <span className="text-[10px] font-medium leading-tight block truncate">
                                {formatTime12(drag.currentStart)}
                              </span>
                              {height >= 28 && (
                                <span className="text-[10px] leading-tight block truncate opacity-80">
                                  {formatTime12(drag.currentEnd)}
                                </span>
                              )}
                            </div>
                          );
                        })()}

                        {/* Drag-to-create preview */}
                        {createDrag && isSameDay(createDrag.date, day) && (() => {
                          const start = createDrag.anchorTime < createDrag.currentTime ? createDrag.anchorTime : createDrag.currentTime;
                          const end = createDrag.anchorTime < createDrag.currentTime ? createDrag.currentTime : createDrag.anchorTime;
                          if (start === end) return null;
                          const top = timeToOffset(start) * CELL_HEIGHT;
                          const height = (timeToOffset(end) - timeToOffset(start)) * CELL_HEIGHT;
                          if (height <= 0) return null;
                          return (
                            <div
                              className="absolute left-1 right-1 rounded-md bg-primary/30 border-2 border-dashed border-primary/60 pointer-events-none z-20"
                              style={{ top: `${top}px`, height: `${height}px` }}
                            >
                              <div className="px-1.5 py-0.5 text-[10px] font-medium text-primary leading-tight">
                                {formatTime12(start)} – {formatTime12(end)}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weekly template (collapsible) */}
      <Collapsible open={weeklyTemplateOpen} onOpenChange={setWeeklyTemplateOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors w-full py-2">
            <ChevronRight size={16} className={cn("transition-transform", weeklyTemplateOpen && "rotate-90")} />
            Weekly Template
            <span className="text-xs text-muted-foreground font-normal ml-1">
              (recurring default hours)
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="border-border bg-card">
            <CardContent className="p-5 space-y-1">
              {DAY_SHORT.map((day, i) => {
                const blocks = weekly[i];
                return (
                  <div key={day} className="py-2 border-b border-border/50 last:border-0">
                    <div className="flex items-start gap-3">
                      <div className="w-10 text-sm font-medium text-muted-foreground pt-1.5">{day}</div>
                      <div className="flex-1 space-y-1.5">
                        {blocks.length === 0 && (
                          <span className="text-xs text-muted-foreground italic leading-8">Unavailable</span>
                        )}
                        {blocks.map((block) => (
                          <div key={block.id} className="flex items-center gap-2">
                            <Select
                              value={block.start_time}
                              onValueChange={(v) => updateWeeklyBlock(block.id, v, block.end_time)}
                            >
                              <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {TIME_OPTIONS.map((t) => (
                                  <SelectItem key={t} value={t}>{formatTime12(t)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="text-muted-foreground text-xs">–</span>
                            <Select
                              value={block.end_time}
                              onValueChange={(v) => updateWeeklyBlock(block.id, block.start_time, v)}
                            >
                              <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {TIME_OPTIONS.map((t) => (
                                  <SelectItem key={t} value={t}>{formatTime12(t)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <button
                              onClick={() => removeWeeklyBlock(block.id)}
                              className="text-muted-foreground hover:text-destructive transition-colors p-1"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addWeeklyBlock(i)}
                        className="h-8 w-8 p-0 shrink-0"
                        title="Add time block"
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default ProviderAvailability;
